import { useState } from 'react';
import { Button } from 'components/core';
import { OUTLINE_SMALL } from './buttonProps';

interface BuilderModeBarProps {
  builderMode: boolean;
  /** Whether the signed-in user may switch to raw JSON. */
  canUseJson: boolean;
  onSwitchToJson: () => void;
  /** Present only when there are builder answers to go back to. Returns false when the KPI cannot be shown as parts. */
  onSwitchToBuilder?: () => boolean | void;
}

// Sits above the tabs. Builder users never see JSON; people allowed to can switch between the two views.
export const BuilderModeBar = ({ builderMode, canUseJson, onSwitchToJson, onSwitchToBuilder }: BuilderModeBarProps) => {
  const [refused, setRefused] = useState(false);
  if (!canUseJson && builderMode) return null;

  const onClick = () => {
    if (builderMode) onSwitchToJson();
    else setRefused(onSwitchToBuilder?.() === false);
  };

  return (
    <div className="mb-3">
      <div className="alert alert-info small py-2 mb-2" role="note">
        {builderMode ? (
          <>
            <strong>Simple KPI?</strong> Use the builder: counts, top-N rankings, trends over time and percentages are built for you from the
            questions below. <strong>Need something the builder cannot do?</strong> Switch to <strong>the JSON editor</strong> with the button below, for
            example a formula such as <strong>completed ÷ registered × 100</strong> across two queries, a custom Elasticsearch aggregation or
            script, several queries combined in one chart, or a drill-down.
          </>
        ) : (
          <>
            <strong>Advanced mode.</strong> You write the query and config by hand, which is the way to build a formula, a custom aggregation,
            or several queries in one chart. <strong>For a simple KPI, go back to the builder:</strong> it is quicker and cannot produce an
            invalid query.
          </>
        )}
      </div>
      <div className="d-flex justify-content-between align-items-center small text-secondary">
        {builderMode ? (
          <>
            <span>The query and config are generated for you as you answer.</span>
            <Button {...OUTLINE_SMALL} onClick={onClick}>
              Edit as JSON (advanced)
            </Button>
          </>
        ) : (
          <>
            <span>
              {onSwitchToBuilder && 'Going back to the builder shows your answers, or for a hand-written KPI reads its queries into parts; changing one replaces any JSON edits.'}
              {refused && ' Something in this KPI cannot be shown as parts, so it stays in JSON.'}
            </span>
            {onSwitchToBuilder && (
              <Button {...OUTLINE_SMALL} onClick={onClick}>
                Switch to builder
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};
